import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { PropertyDetails } from "@/types/property";
import { firebaseService } from "@/lib/firebase";
import type { IdxConsentReceipt } from "../config/idxConsentReceipts";

export interface FirebaseIdentity {
  uid: string;
  email: string | null;
  displayName: string | null;
}

// Registration form data handed from the register step to profile resolution
// after the Firebase sign-in round-trip. Kept in-memory (not localStorage) so it
// is session-scoped and can't go stale across visits.
export interface PendingRegistration {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  marketingConsent: boolean;
  consentReceipts?: IdxConsentReceipt[];
  gaClientId?: string;
  agent?: string;
  tenantId?: string;
}

interface AuthState {
  // Firebase identity — the single source of truth for "who is signed in".
  firebaseUser: FirebaseIdentity | null;
  // True until the first onAuthStateChanged callback fires (initial session check).
  isInitializing: boolean;
  error: Error | null;
  isInGoogleSignInFlow: boolean;
  pendingPropertySave?: PropertyDetails;
  pendingRegistration: PendingRegistration | null;

  // identity — driven by AuthInitializer's single onAuthStateChanged subscription
  setFirebaseUser: (identity: FirebaseIdentity | null) => void;
  setInitialized: () => void;

  setPendingRegistration: (data: PendingRegistration | null) => void;
  clearPendingRegistration: () => void;

  // auth actions
  login: (
    credentials: { email: string; password: string },
    propertyToSave?: PropertyDetails
  ) => Promise<void>;
  loginWithGoogle: (propertyToSave?: PropertyDetails) => Promise<void>;
  logout: () => Promise<void>;

  setIsInGoogleSignInFlow: (value: boolean) => void;
  setPendingPropertySave: (property?: PropertyDetails) => void;
  clearPendingPropertySave: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUser: null,
  isInitializing: true,
  error: null,
  isInGoogleSignInFlow: false,
  pendingPropertySave: undefined,
  pendingRegistration: null,

  setFirebaseUser: (identity) => set({ firebaseUser: identity }),
  setInitialized: () => set({ isInitializing: false }),

  setPendingRegistration: (data) => set({ pendingRegistration: data }),
  clearPendingRegistration: () => set({ pendingRegistration: null }),

  login: async (credentials, propertyToSave) => {
    set({ error: null, pendingPropertySave: propertyToSave });
    try {
      await firebaseService.auth.signIn(credentials);
      // The profile resolves reactively via the useContactProfile query once
      // onAuthStateChanged updates firebaseUser.
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  loginWithGoogle: async (propertyToSave) => {
    set({ error: null, pendingPropertySave: propertyToSave });
    try {
      await firebaseService.auth.signInWithGoogle();
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  logout: async () => {
    set({ error: null });
    try {
      await firebaseService.auth.signOut();
      set({ firebaseUser: null, pendingRegistration: null });
    } catch (error) {
      set({ error: error as Error });
      throw error;
    }
  },

  setIsInGoogleSignInFlow: (value) => set({ isInGoogleSignInFlow: value }),
  setPendingPropertySave: (property) => set({ pendingPropertySave: property }),
  clearPendingPropertySave: () => set({ pendingPropertySave: undefined }),
}));

// Stable action bag for components (identity/profile reads live in useAuth.ts).
export const useAuthActions = () =>
  useAuthStore(
    useShallow((s) => ({
      login: s.login,
      loginWithGoogle: s.loginWithGoogle,
      logout: s.logout,
      setIsInGoogleSignInFlow: s.setIsInGoogleSignInFlow,
      setPendingPropertySave: s.setPendingPropertySave,
      clearPendingPropertySave: s.clearPendingPropertySave,
    }))
  );

export const useFirebaseUser = () => useAuthStore((s) => s.firebaseUser);
